import {
  getV1CompaniesCompanyIdSettlementsSettlementIdOutboundEmails,
  postV1CompaniesCompanyIdSettlementsEmails,
  SettlementEmail_Item_Create,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';

import { OutboundEmail } from './models';
import sendOutboundEmails, {
  OutboundEmailEntry,
  outboundEmailFields,
} from './sendOutboundEmails';

interface SendSettlementEmailParams {
  companyId: string;
  emails: OutboundEmailEntry[];
}

export default function useSettlementOutboundEmails() {
  const getSettlementOutboundEmails = useCallback(
    async (
      companyId: string,
      settlementId: string,
      link?: PaginationLink,
      cursor?: string,
    ) => {
      try {
        const response =
          await getV1CompaniesCompanyIdSettlementsSettlementIdOutboundEmails({
            path: { 'company-id': companyId, 'settlement-id': settlementId },
            query: {
              'page[limit]': MAX_API_PAGE_SIZE,
              ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
            },
          });

        return {
          data: response.data.data.map(OutboundEmail.parse),
          pagination: extractPagination(response),
        };
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.settlement_outbound_emails.failed_to_fetch'),
        );
        throw error;
      }
    },
    [],
  );

  const sendSettlementEmail = useCallback(
    ({ companyId, emails }: SendSettlementEmailParams) =>
      sendOutboundEmails({
        errorMessage: t('error_messages.settlements.failed_to_request_settlement_email'),
        send: () =>
          postV1CompaniesCompanyIdSettlementsEmails({
            path: { 'company-id': companyId },
            body: {
              emails: emails.map((entry): SettlementEmail_Item_Create => ({
                ...outboundEmailFields(entry),
                settlement_ids: entry.ids,
              })),
            },
          }),
      }),
    [],
  );

  return { getSettlementOutboundEmails, sendSettlementEmail };
}
